import { isoWeekdayOf } from '@app/hr/hr-schedule-engine';
import type { Day } from './rental-billing.model';

// Jours de Paris « AAAA-MM-JJ » en arithmétique civile pure (algorithme de H. Hinnant) : ni horloge, ni fuseau du poste.
const WEEKDAYS = ['lun.', 'mar.', 'mer.', 'jeu.', 'ven.', 'sam.', 'dim.'];
const MONTHS = ['janvier', 'février', 'mars', 'avril', 'mai', 'juin', 'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre'];
const MONTHS_SHORT = ['janv.', 'févr.', 'mars', 'avr.', 'mai', 'juin', 'juil.', 'août', 'sept.', 'oct.', 'nov.', 'déc.'];
const MONEY = new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const QTY = new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 2 });

function partsOf(day: Day): [number, number, number] {
  return [Number(day.slice(0, 4)), Number(day.slice(5, 7)), Number(day.slice(8, 10))];
}

function pad2(n: number): string {
  return (n < 10 ? '0' : '') + n;
}

function dayNumber(day: Day): number {
  const [y0, m, d] = partsOf(day);
  const y = m <= 2 ? y0 - 1 : y0;
  const era = Math.floor(y / 400);
  const yoe = y - era * 400;
  const doy = Math.floor((153 * (m + (m > 2 ? -3 : 9)) + 2) / 5) + d - 1;
  const doe = yoe * 365 + Math.floor(yoe / 4) - Math.floor(yoe / 100) + doy;
  return era * 146097 + doe - 719468;
}

function dayOfNumber(n: number): Day {
  const z = n + 719468;
  const era = Math.floor(z / 146097);
  const doe = z - era * 146097;
  const yoe = Math.floor((doe - Math.floor(doe / 1460) + Math.floor(doe / 36524) - Math.floor(doe / 146096)) / 365);
  const doy = doe - (365 * yoe + Math.floor(yoe / 4) - Math.floor(yoe / 100));
  const mp = Math.floor((5 * doy + 2) / 153);
  const d = doy - Math.floor((153 * mp + 2) / 5) + 1;
  const m = mp + (mp < 10 ? 3 : -9);
  const y = yoe + era * 400 + (m <= 2 ? 1 : 0);
  return String(y).padStart(4, '0') + '-' + pad2(m) + '-' + pad2(d);
}

export function isDay(s: unknown): s is Day {
  if (typeof s !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return false;
  return dayOfNumber(dayNumber(s)) === s;
}

export function addDays(day: Day, n: number): Day {
  return dayOfNumber(dayNumber(day) + n);
}

// b − a en jours : positif si b est après a.
export function daysBetween(a: Day, b: Day): number {
  return dayNumber(b) - dayNumber(a);
}

export function minDay(...days: (Day | null | undefined)[]): Day | null {
  let best: Day | null = null;
  for (const d of days) if (d && (best === null || d < best)) best = d;
  return best;
}

export function maxDay(...days: (Day | null | undefined)[]): Day | null {
  let best: Day | null = null;
  for (const d of days) if (d && (best === null || d > best)) best = d;
  return best;
}

export function firstOfMonth(day: Day): Day {
  return day.slice(0, 8) + '01';
}

export function daysInMonth(year: number, month: number): number {
  const next = month === 12 ? `${String(year + 1).padStart(4, '0')}-01-01` : `${String(year).padStart(4, '0')}-${pad2(month + 1)}-01`;
  return daysBetween(`${String(year).padStart(4, '0')}-${pad2(month)}-01`, next);
}

// Même quantième k mois plus loin, butée en fin de mois (31/01 + 1 = 28/02), comme checked_add_months du serveur.
export function addMonthsClamped(day: Day, k: number): Day {
  const [y, m, d] = partsOf(day);
  const total = y * 12 + (m - 1) + k;
  const ny = Math.floor(total / 12);
  const nm = total - ny * 12 + 1;
  return `${String(ny).padStart(4, '0')}-${pad2(nm)}-${pad2(Math.min(d, daysInMonth(ny, nm)))}`;
}

export function frDay(day: Day | null | undefined): string {
  return day ? `${day.slice(8, 10)}/${day.slice(5, 7)}/${day.slice(0, 4)}` : '';
}

export function frShort(day: Day | null | undefined): string {
  return day ? `${day.slice(8, 10)}/${day.slice(5, 7)}` : '';
}

export function frWeekday(day: Day | null | undefined): string {
  return day ? `${WEEKDAYS[isoWeekdayOf(day) - 1]} ${frShort(day)}` : '';
}

export function frMonth(first: Day): string {
  const [y, m] = partsOf(first);
  return `${MONTHS[m - 1]} ${y}`;
}

export function frMonthShort(first: Day): string {
  return MONTHS_SHORT[partsOf(first)[1] - 1];
}

// Arrondi symétrique au centime : un −0,001 s'affiche 0,00, jamais « -0,00 ».
function cents(n: number): number {
  const r = (Math.sign(n) * Math.round(Math.abs(n) * 100)) / 100;
  return r === 0 ? 0 : r;
}

export function money(n: number | null | undefined): string {
  return MONEY.format(cents(Number(n) || 0));
}

export function signedMoney(n: number | null | undefined): string {
  const r = cents(Number(n) || 0);
  return (r < 0 ? '−' : r > 0 ? '+' : '') + MONEY.format(Math.abs(r));
}

export function qtyFr(n: number | null | undefined): string {
  const r = cents(Number(n) || 0);
  return QTY.format(r);
}
